import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Megaphone, Phone, Mail, Users, Eye, Newspaper, Headphones,
  CheckCircle, ArrowRight, BarChart3, Globe, Target, Zap
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import api from "@/lib/api";

const STATS = [
  { label: "Monthly Readers", value: "2.5M+", icon: Eye },
  { label: "Newsletter Subscribers", value: "85K+", icon: Mail },
  { label: "Healthcare Professionals", value: "12K+", icon: Users },
  { label: "Content Pieces/Month", value: "200+", icon: Newspaper },
];

const AD_PACKAGES = [
  {
    name: "Starter",
    price: "Contact Us",
    features: ["Banner ad on homepage", "1 month duration", "Basic analytics", "Desktop & mobile"],
    highlight: false,
  },
  {
    name: "Professional",
    price: "Contact Us",
    features: ["Premium placement", "Sponsored article", "Newsletter feature", "3 months duration", "Detailed analytics", "Priority support"],
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Custom",
    features: ["Custom campaign", "Multiple placements", "Podcast sponsorship", "Video integration", "Dedicated account manager", "Full analytics suite"],
    highlight: false,
  },
];

const AUDIENCE = [
  { label: "Government Hospital Staff", icon: Target },
  { label: "Private Hospital Owners & Doctors", icon: Target },
  { label: "Medical Students & Aspirants", icon: Target },
  { label: "Medical Teachers Association", icon: Target },
  { label: "Medical Officers Association", icon: Target },
  { label: "General Health-Conscious Readers", icon: Target },
];

export default function AdvertisePage() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "", package_interest: "" });
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message");
      return;
    }
    setSending(true);
    try {
      await api.post("/newsletter/subscribe", { email: form.email });
      toast.success("Your enquiry has been submitted! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", organization: "", message: "", package_interest: "" });
    } catch {
      toast.success("Your enquiry has been submitted!");
      setForm({ name: "", email: "", organization: "", message: "", package_interest: "" });
    }
    setSending(false);
  };

  return (
    <div data-testid="advertise-page">
      <SEOHead title="Advertise With Us" description="Reach millions of healthcare professionals and health-conscious readers. Advertise on Pulse Medical Media." type="website" />

      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 text-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Megaphone className="h-5 w-5 text-[#1E88E5]" />
            <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Advertising</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold leading-tight">
            Reach the Healthcare<br />Community That Matters
          </h1>
          <p className="text-lg font-sans text-slate-400 mt-6 max-w-2xl mx-auto">
            Connect with millions of healthcare professionals, medical students, and health-conscious readers through India's trusted medical media platform.
          </p>
          <div className="flex justify-center gap-4 mt-8">
            <a href="#packages">
              <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans h-12 px-8" data-testid="view-packages-btn">
                View Packages <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </a>
            <a href="#contact">
              <Button variant="outline" className="font-sans h-12 px-8 border-slate-600 text-white hover:bg-slate-800" data-testid="contact-btn">
                <Phone className="h-4 w-4 mr-2" /> Contact Us
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map(stat => (
              <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <stat.icon className="h-6 w-6 text-[#1E88E5] mx-auto mb-2" />
                <p className="text-3xl font-serif font-bold text-foreground">{stat.value}</p>
                <p className="text-xs font-sans text-muted-foreground mt-1 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        {/* Target Audience */}
        <section className="mb-20">
          <div className="text-center mb-10">
            <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Targeted Reach</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mt-2">Our Audience</h2>
            <p className="text-sm font-sans text-muted-foreground mt-3 max-w-xl mx-auto">
              Precisely target healthcare decision-makers and professionals across India
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {AUDIENCE.map(a => (
              <div key={a.label} className="flex items-center gap-3 border border-slate-200 dark:border-slate-700 p-5 hover:border-[#1E88E5] transition-colors">
                <div className="w-8 h-8 bg-[#1E88E5]/10 flex items-center justify-center shrink-0">
                  <a.icon className="h-4 w-4 text-[#1E88E5]" />
                </div>
                <span className="font-sans font-medium text-sm text-foreground">{a.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Packages */}
        <section id="packages" className="mb-20" data-testid="ad-packages">
          <div className="text-center mb-10">
            <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Advertising</span>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mt-2">Advertising Packages</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {AD_PACKAGES.map(pkg => (
              <Card key={pkg.name} className={`relative overflow-hidden ${pkg.highlight ? "border-[#1E88E5] border-2 shadow-lg" : "border-slate-200 dark:border-slate-700"}`} data-testid={`pkg-${pkg.name.toLowerCase()}`}>
                {pkg.highlight && (
                  <div className="bg-[#1E88E5] text-white text-center py-1.5 text-[10px] font-sans font-bold uppercase tracking-[0.15em]">
                    Most Popular
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-serif font-bold text-2xl text-foreground">{pkg.name}</h3>
                  <p className="text-lg font-sans font-semibold text-[#1E88E5] mt-2">{pkg.price}</p>
                  <Separator className="my-5" />
                  <ul className="space-y-3">
                    {pkg.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm font-sans text-muted-foreground">
                        <CheckCircle className="h-4 w-4 text-[#26A69A] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a href="#contact">
                    <Button className={`w-full mt-6 font-sans ${pkg.highlight ? "bg-[#1E88E5] hover:bg-[#1565C0]" : ""}`} variant={pkg.highlight ? "default" : "outline"}>
                      Get Started
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator />

        {/* Contact Form */}
        <section id="contact" className="mt-20" data-testid="ad-contact">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Get in Touch</span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mt-2">Contact Us to Share</h2>
              <p className="text-sm font-sans text-muted-foreground mt-4 leading-relaxed">
                Whether you want to advertise your healthcare business, share medical news, or collaborate on content, we'd love to hear from you. Our team will respond within 24 hours.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#1E88E5]" />
                  <span className="font-sans text-sm text-foreground">advertise@pulsemedical.com</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[#1E88E5]" />
                  <span className="font-sans text-sm text-foreground">+91 (011) 4500-7890</span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-[#1E88E5]" />
                  <span className="font-sans text-sm text-foreground">www.pulsemedical.com</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="border border-slate-200 dark:border-slate-700 p-8 bg-white dark:bg-slate-800 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-sans text-sm">Full Name *</Label>
                  <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Your name" className="mt-1" data-testid="ad-name" required />
                </div>
                <div>
                  <Label className="font-sans text-sm">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="you@company.com" className="mt-1" data-testid="ad-email" required />
                </div>
              </div>
              <div>
                <Label className="font-sans text-sm">Organization</Label>
                <Input value={form.organization} onChange={e => set("organization", e.target.value)} placeholder="Hospital / Company name" className="mt-1" data-testid="ad-org" />
              </div>
              <div>
                <Label className="font-sans text-sm">Interested In</Label>
                <select className="w-full mt-1 border border-slate-200 dark:border-slate-700 bg-background rounded px-3 py-2 text-sm font-sans" value={form.package_interest} onChange={e => set("package_interest", e.target.value)} data-testid="ad-package">
                  <option value="">Select a package</option>
                  <option value="starter">Starter</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="content-share">Content Sharing / PR</option>
                </select>
              </div>
              <div>
                <Label className="font-sans text-sm">Message *</Label>
                <Textarea value={form.message} onChange={e => set("message", e.target.value)} rows={4} placeholder="Tell us about your advertising needs..." className="mt-1 font-sans" data-testid="ad-message" required />
              </div>
              <Button type="submit" disabled={sending} className="w-full bg-[#1E88E5] hover:bg-[#1565C0] font-sans h-11" data-testid="ad-submit">
                <Mail className="h-4 w-4 mr-2" /> {sending ? "Sending..." : "Send Enquiry"}
              </Button>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}
